import { ENV } from '../config/env';

/**
 * Cliente del daemon local de firma para el flujo de firma individual (#59, F1C, §1C.7 Slice B).
 *
 * Habla con el cliente local (boveda-signature-client / LocalSignatureServer) por loopback, sin
 * auth (no pasa por el axios autenticado de la API): {@code POST /tokens} (enumera los certs del
 * token, requiere PIN) y {@code POST /sign-batch} (firma digests, ADR-030). El status del daemon se consulta con
 * {@code getLocalClientStatus} (servicio aparte). Errores del daemon → {@code {success:false, reason}}.
 */

const TOKENS_TIMEOUT_MS = 20000;
const SIGN_BATCH_TIMEOUT_MS = 300000; // 5 min: 1 sesión de token, N firmas

const base = () => (ENV.SIGNATURE_CLIENT_DAEMON_URL || '').replace(/\/+$/, '');

/** Mensajes amigables para los {@code reason} que devuelve el daemon (Pkcs11Errors). */
const DAEMON_REASON_MESSAGES = {
  pin_incorrect: 'El PIN ingresado es incorrecto.',
  pin_locked: 'El PIN está bloqueado. Desbloqueá el token con su herramienta de gestión.',
  pin_expired: 'El PIN está vencido y debe renovarse.',
  token_not_present: 'No se detecta el token. Conectalo y volvé a intentar.',
  // #obs: este mensaje MENTÍA. Cuando el driver instalado es otro (p. ej. Feitian en vez de
  // mToken), reiniciar el cliente no arregla nada: falta el middleware o el token no está puesto.
  module_not_found: 'No se encontró el driver del token en esta máquina, o ningún token está conectado. '
    + 'Verificá que el token esté enchufado y que su middleware esté instalado. '
    + 'Abrí el cliente de firma: la ventana muestra qué middlewares detectó.',
  module_unavailable: 'El driver del token está instalado pero no se pudo cargar. '
    + 'Desconectá y volvé a conectar el token; si sigue, revisá la ventana del cliente de firma.',
  certificate_expired: 'El certificado del token está vencido.',
  certificate_not_yet_valid: 'El certificado del token todavía no es válido.',
  sign_error: 'No se pudo firmar con el token. Reinsertá el token y reiniciá el cliente de firma.',
  missing_fields: 'Faltan datos para firmar.',
  unsupported_level: 'El nivel de firma solicitado no está soportado por el cliente.',
  origin_not_authorized: 'Este sitio todavía no está autorizado en el cliente de firma. Autorizalo en la ventana del cliente y reintentá.',
};

/** Traduce un error del daemon (o de red) a un mensaje para el usuario. */
export const daemonErrorMessage = (err) => {
  const reason = err?.reason || err?.daemon?.reason;
  if (reason && DAEMON_REASON_MESSAGES[reason]) return DAEMON_REASON_MESSAGES[reason];
  if (err?.name === 'AbortError') return 'El cliente local de firma no respondió a tiempo.';
  // El daemon respondió con un error (hay status/cuerpo): NO es un problema de conexión, así que
  // no decimos "verificá que esté en ejecución" (engaña). Mostramos el detalle del daemon si lo hay.
  if (err?.status || err?.daemon) {
    const detail = err?.daemon?.expected || err?.daemon?.detail || reason;
    return `El cliente local de firma rechazó la operación${detail ? `: ${detail}` : '.'}`;
  }
  return 'No se pudo comunicar con el cliente local de firma. Verificá que esté en ejecución.';
};

async function postJson(path, body, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${base()}${path}`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || data?.success === false) {
      const err = new Error(`daemon ${path} → ${res.status} (${data?.reason || 'error'})`);
      err.reason = data?.reason;
      err.status = res.status;
      err.daemon = data;
      throw err;
    }
    return data;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Enumera los certificados del token (requiere PIN: PKCS#11 necesita login para listar las claves).
 * @returns {Promise<Array<{alias,subject,issuer,serial,notBefore,notAfter}>>}
 */
export const listTokens = async ({ pin, tokenType = 'pkcs11', pkcs11ModulePath } = {}) => {
  const data = await postJson('/tokens', { pin, tokenType, pkcs11ModulePath }, TOKENS_TIMEOUT_MS);
  return data?.tokens ?? [];
};

/**
 * Firma N digests en una sola sesión de token (1 PIN → N firmas external-CMS). El cert NUNCA sale
 * de la máquina; vuelven solo los blobs CMS. Aislamiento por-digest: un digest malo entra en
 * {@code failures}, no tumba el lote.
 * @param {{digests:Array<{docId:number,messageDigestHex:string,signingDate:string}>, pin:string,
 *          certAlias:string, tokenType?:string, pkcs11ModulePath?:string}} args
 * @returns {Promise<{results:Array<{docId:number,cmsBase64:string}>, failures:Array<{docId:number,error:string}>}>}
 */
export const signBatch = async ({
  digests, pin, certAlias, tokenType = 'pkcs11', pkcs11ModulePath,
}) => {
  return postJson(
    '/sign-batch',
    { digests, pin, certAlias, tokenType, pkcs11ModulePath },
    SIGN_BATCH_TIMEOUT_MS
  );
};

/**
 * Consulta si este origen web ya está autorizado en el daemon (ADR-033).
 * GET /authorize → 200 {authorized:true} | 403 {reason:'origin_not_authorized', origin}.
 * Cualquier otro fallo (timeout, red, status inesperado) se relanza como error real.
 * @returns {Promise<{authorized:boolean, origin?:string}>}
 */
export const getAuthorizationStatus = async (timeoutMs = 3000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${base()}/authorize`, { method: 'GET', mode: 'cors', signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.authorized) return { authorized: true };
    if (res.status === 403 && data?.reason === 'origin_not_authorized') {
      return { authorized: false, origin: data.origin };
    }
    const err = new Error('authorize_failed');
    err.status = res.status;
    err.reason = data?.reason;
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

// ── Helper base64 → bytes: los CMS que devuelve el daemon vienen en base64 ──

export const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
};
